// src/pages/DailyOraclePage.js

import React, { useState, useEffect } from 'react';
import DailyOracleDeck from '../../components/decks/DailyOracleDeck'; // Ajusta la ruta
import UrlConfig from '../../data/UrlConfig'; // Ajusta la ruta
import { useGlow } from '../../hooks/useGlow'; // Ajusta la ruta
// Si estás usando react-router-dom para la navegación, importa Link:
import { Link } from 'react-router-dom';
import { PlayCircleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid'; // Iconos para los nuevos elementos


// --- Imports de las imágenes de los símbolos (necesarias para ambos decks) ---
import symbol1 from '../../assets/bashar/sacred-circuitry/deck/1.png';
import symbol2 from '../../assets/bashar/sacred-circuitry/deck/2.png';
import symbol3 from '../../assets/bashar/sacred-circuitry/deck/3.png';
import symbol4 from '../../assets/bashar/sacred-circuitry/deck/4.png';
import symbol5 from '../../assets/bashar/sacred-circuitry/deck/5.png';
import symbol6 from '../../assets/bashar/sacred-circuitry/deck/6.png';
import symbol7 from '../../assets/bashar/sacred-circuitry/deck/7.png';
import symbol8 from '../../assets/bashar/sacred-circuitry/deck/8.png';
import symbol9 from '../../assets/bashar/sacred-circuitry/deck/9.png';
import symbol10 from '../../assets/bashar/sacred-circuitry/deck/10.png';
import symbol11 from '../../assets/bashar/sacred-circuitry/deck/11.png';
import symbol12 from '../../assets/bashar/sacred-circuitry/deck/12.png';
import symbol13 from '../../assets/bashar/sacred-circuitry/deck/13.png';
import symbol14 from '../../assets/bashar/sacred-circuitry/deck/14.png';
import symbol15 from '../../assets/bashar/sacred-circuitry/deck/15.png';
import CommentSection from '../../components/comments/CommentSection';

const symbolImages = [
    symbol1, symbol2, symbol3, symbol4, symbol5,
    symbol6, symbol7, symbol8, symbol9, symbol10,
    symbol11, symbol12, symbol13, symbol14, symbol15
];

const sacredCircuits = [
    { id: 1, name: 'Center', description: 'Represents grounding and alignment with your inner self and universal energy.' },
    { id: 2, name: 'Expand', description: 'Encourages the expansion of your consciousness and the discovery of new horizons.' },
    { id: 3, name: 'Balance', description: 'Symbolizes the need to find equilibrium in all aspects of life.' },
    { id: 4, name: 'Prepare', description: 'Encourages preparation and organization for upcoming changes or opportunities.' },
    { id: 5, name: 'Express', description: 'Supports clear communication and authentic expression of one\'s truth.' },
    { id: 6, name: 'Extend', description: 'Reach out with your energy, connecting with the broader universe and those around you.' },
    { id: 7, name: 'Unlock', description: 'Open the doors of perception, freeing yourself from limiting beliefs and mental constraints.' },
    { id: 8, name: 'Flow', description: 'Surrender to the natural flow of energy, allowing life to unfold effortlessly around you.' },
    { id: 9, name: 'Receive', description: 'Open your being to the gifts of the universe, accepting abundance and wisdom.' },
    { id: 10, name: 'Relate', description: 'Connect deeply with others and the world, fostering understanding and compassion.' },
    { id: 11, name: 'Realize', description: 'Achieve a state of enlightenment, recognizing the truth of your existence and your purpose.' },
    { id: 12, name: 'Act', description: 'Take inspired action in alignment with your highest self, manifesting your intentions.' },
    { id: 13, name: 'Transform', description: 'Embrace change and growth, evolving into a more authentic version of yourself.' },
    { id: 14, name: 'Connect', description: 'Strengthen your bond with the divine and the cosmos, experiencing unity with all that is.' },
    { id: 15, name: 'Manifest', 'description': 'Bring your dreams and desires into reality, making the invisible visible.' },
];

const API_URLS_FOR_DECK = {
    card_draw_log: UrlConfig.card_draw_log,
    card_draw_history: UrlConfig.card_draw_history,
};

const sendCodedLogFunction = (log) => {
    console.log("App-level Logging:", log);
    // Aquí puedes integrar tu sistema de logs real, como Google Analytics, Sentry, etc.
};

const DailyOraclePage = () => {
    const titleStyle = useGlow({
        gradient: 'linear-gradient(to bottom, #bdbcc4ff, #4e4a68ff, #251e47ff, #26187aff)'
    });

    // Simula un email de usuario logueado. En una aplicación real, esto vendría de la autenticación.
    const [loggedInUserEmail, setLoggedInUserEmail] = useState('user@example.com');
    const [showVideo, setShowVideo] = useState(false); // Nuevo estado para controlar la visibilidad del video

    const youtubeVideoId = 'gEwYpvOuzUM'; // ID del video de YouTube

    return (
        <div className="relative bg-black text-white min-h-screen p-8 flex flex-col items-center">
            {/* Overlay para el video */}
            {showVideo && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm p-4">
                    <div className="relative w-full max-w-4xl aspect-video bg-gray-900 rounded-lg shadow-2xl">
                        <button
                            onClick={() => setShowVideo(false)}
                            className="absolute -top-10 right-0 md:-right-10 text-white hover:text-gray-300 transition-colors duration-200 z-50"
                            aria-label="Close video"
                        >
                            <XMarkIcon className="w-8 h-8 md:w-10 md:h-10" />
                        </button>
                        <iframe
                            src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1`}
                            title="Sacred Circuits Explanation"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full rounded-lg"
                        ></iframe>
                    </div>
                </div>
            )}

            <h1 style={titleStyle} className="text-4xl sm:text-5xl font-bold text-center mb-12">My Daily Oracle</h1>

            <DailyOracleDeck
                sacredCircuits={sacredCircuits}
                symbolImages={symbolImages}
                maxDraws={3} // O puedes cambiarlo a 5, por ejemplo
                userEmail={loggedInUserEmail}
                apiUrls={API_URLS_FOR_DECK}
                sendCodedLog={sendCodedLogFunction}
                locationPath="/daily-oracle-page" // Ruta para el logging
            />

            <div className="mt-16 flex flex-col sm:flex-row justify-center items-center gap-6">
                {/* Botón para más información de los circuitos sagrados */}
                <Link
                    to="/sacred-circuitry"
                    className="flex items-center gap-3 py-3 px-6 bg-sky-600 hover:bg-sky-700 rounded-lg font-semibold transition-all duration-300 text-lg"
                >
                    <InformationCircleIcon className="w-6 h-6"/>
                    Learn More About Sacred Circuits
                </Link>

                {/* Botón para el video explicativo */}
                <button
                    onClick={() => setShowVideo(true)}
                    className="flex items-center gap-3 py-3 px-6 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all duration-300 text-lg"
                >
                    <PlayCircleIcon className="w-6 h-6"/>
                    Watch Explanatory Video
                </button>
            </div>
      
            <CommentSection className='mt-20' threadId={'daily-oracle-deck-111'} />

            <footer className="relative z-10 text-center py-6 text-xs text-gray-600 mt-12">
                © {new Date().getFullYear()} My Website | Daily Oracle Page.
            </footer>
        </div>
    );
};

export default DailyOraclePage;